import { useMemo } from 'react';
import { parseISO } from 'date-fns';
import type { Meeting } from '../types/meeting';
import type { MeetingSortField } from '../components/Meetings/MeetingSortControls';
import type { MeetingFilter } from '../components/Meetings/MeetingFilterPanel';

interface SortConfig {
  field: MeetingSortField;
  direction: 'asc' | 'desc';
}

export function useFilteredAndSortedMeetings(
  meetings: Meeting[] | undefined,
  filters: MeetingFilter,
  sortConfig: SortConfig,
  searchQuery: string
) {
  return useMemo(() => {
    if (!meetings) return [];

    let filteredMeetings = meetings;

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredMeetings = filteredMeetings.filter(meeting => 
        meeting.meet_comm?.toLowerCase().includes(query) ||
        (meeting.meet_minutes?.toLowerCase() || '').includes(query)
      );
    }

    // Apply filters
    if (filters.community) {
      filteredMeetings = filteredMeetings.filter(meeting => 
        meeting.meet_comm === filters.community
      );
    }

    if (filters.year) {
      filteredMeetings = filteredMeetings.filter(meeting => 
        meeting.meet_date.substring(0, 4) === filters.year
      );
    }

    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filteredMeetings = filteredMeetings.filter(meeting => 
        parseISO(meeting.meet_date) >= fromDate
      );
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      filteredMeetings = filteredMeetings.filter(meeting => 
        parseISO(meeting.meet_date) <= toDate
      );
    }

    // Apply sorting
    const sortedMeetings = [...filteredMeetings].sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;

      switch (sortConfig.field) {
        case 'date':
          return direction * (parseISO(a.meet_date).getTime() - parseISO(b.meet_date).getTime());
        case 'community':
          return direction * ((a.meet_comm || '').localeCompare(b.meet_comm || ''));
        default:
          return 0;
      }
    });

    return sortedMeetings;
  }, [meetings, filters, sortConfig, searchQuery]);
}
